import { graphql } from '@/gql';
import {
  CommonGetCompanyUsersQueryVariables,
  CommonDeleteUserMutationVariables,
  CommonRequestUserPasswordResetMutationVariables,
  CommonAddUserMutationVariables,
  CommonSaveCompanyDataMutationVariables,
  CommonFetchCompanyStationsQueryVariables,
  CommonDownloadReportQueryVariables,
  CommonGetCompanyOrdersQueryVariables,
  SetOrganizationModeMutationVariables,
  GetCompanyContractsDatesQueryVariables,
} from '@/gql/graphql';
import useQuery from '@/helpers/useQuery';

export async function getCompanyUsers(v: CommonGetCompanyUsersQueryVariables) {
  const q = graphql(`
    query CommonGetCompanyUsers($companyId: ID, $partnerId: ID, $contractId: ID, $query: String) {
      cabinet {
        company(id: $companyId, partnerId: $partnerId, contractId: $contractId) {
          users(query: $query) {
            count
            items {
              id
              phone
              ... on LKUser {
                name
                email
                role
                access {
                  id
                  partners {
                    id
                    name
                  }
                }
                lastActivity
              }
            }
          }
          userAccess {
            partnerParentId
            partnerId
            name
            role
          }
        }
      }
    }
  `);

  return await useQuery(q, v);
}

export async function addUser(v: CommonAddUserMutationVariables) {
  const q = graphql(`
    mutation CommonAddUser($companyId: ID!, $user: UserData!) {
      addUser(companyId: $companyId, data: $user)
    }
  `);

  return (await useQuery(q, v)).addUser;
}

export async function requestUserPasswordReset(v: CommonRequestUserPasswordResetMutationVariables) {
  const q = graphql(`
    mutation CommonRequestUserPasswordReset($companyId: ID!, $userId: ID!) {
      sendRestoreUserPasswordEmail(companyId: $companyId, id: $userId)
    }
  `);

  return (await useQuery(q, v)).sendRestoreUserPasswordEmail;
}

export async function deleteUser(v: CommonDeleteUserMutationVariables) {
  const q = graphql(`
    mutation CommonDeleteUser($companyId: ID!, $userId: ID!) {
      removeUser(companyId: $companyId, id: $userId)
    }
  `);

  return (await useQuery(q, v)).removeUser;
}

export async function saveCompanyData(v: CommonSaveCompanyDataMutationVariables) {
  const q = graphql(`
    mutation CommonSaveCompanyData($companyId: ID, $contractId: ID, $data: CompanyData!) {
      saveCompany(companyId: $companyId, contractId: $contractId, data: $data) {
        result
        message
      }
    }
  `);

  return await useQuery(q, v);
}

export async function fetchCompanyStations(v: CommonFetchCompanyStationsQueryVariables) {
  const q = graphql(`
    query CommonFetchCompanyStations(
      $companyId: ID!
      $contractsType: String!
      $page: Int
      $size: Int
      $query: String
      $status: StationStatus
      $partnerId: ID
    ) {
      cabinet {
        company(id: $companyId) {
          stations(
            page: $page
            size: $size
            query: $query
            contractsType: $contractsType
            status: $status
            partnerId: $partnerId
          ) {
            count
            items {
              id
              name
              address
              lastOnline
              certificate
              certificateKey
              certificateExpiration
            }
            status {
              status
              count
            }
          }
        }
      }
    }
  `);

  return await useQuery(q, v);
}

export async function getCompanyOrdersFn(v: CommonGetCompanyOrdersQueryVariables) {
  const q = graphql(`
    query CommonGetCompanyOrders($companyId: ID, $contractId: ID, $partnerId: ID, $filter: OrdersQueryParams!) {
      cabinet {
        company(id: $companyId, partnerId: $partnerId, contractId: $contractId) {
          ordersNew(filter: $filter) {
            count
            items {
              id
              type
              date
              status
              statusMessage
              user {
                phone
              }
              corp {
                driver {
                  name
                  phone
                }
                car {
                  number
                }
              }
              station {
                name
                address
              }
              ... on OnlineOrder {
                item {
                  fuel {
                    name
                  }
                  quantityReport
                  quantityReq
                  costReport {
                    fact
                    station
                    corp
                  }
                  costReq {
                    fact
                    station
                    corp
                  }
                  prices {
                    fact
                    station
                    corp
                  }
                  pump
                }
              }
            }
            summary {
              name
              count
              quantity
              total
              discount
            }
          }
        }
      }
    }
  `);

  return useQuery(q, v).then((response) => response ?? { cabinet: null });
}

export async function downloadOrdersReportFn(v: CommonDownloadReportQueryVariables) {
  const q = graphql(`
    query CommonDownloadReport($companyId: ID, $contractId: ID, $partnerId: ID, $filter: OrdersQueryParams!) {
      cabinet {
        company(id: $companyId, contractId: $contractId, partnerId: $partnerId) {
          ordersNewReportLink(format: excel, filter: $filter)
        }
      }
    }
  `);

  return useQuery(q, v).then((response) => response.cabinet?.company?.ordersNewReportLink ?? '');
}

export async function setOrganizationMode(v: SetOrganizationModeMutationVariables) {
  const q = graphql(`
    mutation SetOrganizationMode($companyId: ID!, $mode: OrganizationMode!) {
      setOrganizationMode(companyId: $companyId, mode: $mode)
    }
  `);

  return await useQuery(q, v);
}

export async function getCompanyContractsDates(v: GetCompanyContractsDatesQueryVariables) {
  const q = graphql(`
    query getCompanyContractsDates($companyId: ID) {
      cabinet {
        company(id: $companyId) {
          contracts {
            id
            date
            finishDate
          }
        }
      }
    }
  `);

  return useQuery(q, v);
}
